/**
 * Measures of association from a 2 × 2 table.
 *
 *                 Outcome   No outcome
 *     Exposed        a          b
 *     Unexposed      c          d
 *
 * - Odds ratio ad/bc with Woolf's logit interval: SE(ln OR) = √(1/a + 1/b + 1/c + 1/d).
 * - Risk ratio [a/(a + b)]/[c/(c + d)] with Katz's log interval:
 *   SE(ln RR) = √(1/a − 1/(a + b) + 1/c − 1/(c + d)).
 * - Risk difference with the Wald interval, and the number needed to treat or harm,
 *   1/|RD|, with Altman's (1998) interval from the reciprocals of the RD limits.
 *
 * Zero cells: where a zero makes a ratio or its standard error undefined, 0.5 is added
 * to all four cells for that measure (the Haldane–Anscombe correction) — any zero for
 * the odds ratio, a zero in a or c for the risk ratio. The result says when it happened.
 */

import { normalUpper } from "@/lib/stats/distributions";
import { type Interval, zFor } from "@/lib/tool-kit/stats-research/proportions";

export interface RatioEstimate extends Interval {
  value: number;
  /** SE of the natural log of the ratio. */
  logSe: number;
  z: number;
  p: number;
  /** True when 0.5 was added to every cell. */
  corrected: boolean;
}

export interface NumberNeeded {
  /** 1/|RD|. Infinite when RD is 0. */
  value: number;
  /** "harm" when exposure adds outcomes (RD > 0), "benefit" when it removes them. */
  direction: "harm" | "benefit" | "none";
  /**
   * When the RD interval excludes 0: a single interval on the same side. When it spans
   * 0: the interval runs from `towardsBenefit` (fewer outcomes) through infinity to
   * `towardsHarm` (more outcomes).
   */
  interval:
    | { kind: "bounded"; lower: number; upper: number }
    | { kind: "split"; towardsBenefit: number; towardsHarm: number };
}

export interface RiskResult {
  a: number;
  b: number;
  c: number;
  d: number;
  riskExposed: number;
  riskUnexposed: number;
  oddsRatio: RatioEstimate | null;
  riskRatio: RatioEstimate | null;
  riskDifference: (Interval & { value: number; se: number }) | null;
  numberNeeded: NumberNeeded | null;
}

function ratioEstimate(
  value: number,
  logSe: number,
  corrected: boolean,
  z: number
): RatioEstimate {
  const log = Math.log(value);
  const statistic = log / logSe;
  return {
    value,
    logSe,
    lower: Math.exp(log - z * logSe),
    upper: Math.exp(log + z * logSe),
    z: statistic,
    p: Math.min(1, 2 * normalUpper(Math.abs(statistic))),
    corrected,
  };
}

/** Null when a whole row or column is empty — the ratio carries no information. */
export function oddsRatio(
  a: number,
  b: number,
  c: number,
  d: number,
  level = 0.95
): RatioEstimate | null {
  if (a + b === 0 || c + d === 0 || a + c === 0 || b + d === 0) {
    return null;
  }
  const corrected = Math.min(a, b, c, d) === 0;
  const [x, y, u, v] = corrected
    ? [a + 0.5, b + 0.5, c + 0.5, d + 0.5]
    : [a, b, c, d];
  return ratioEstimate(
    (x * v) / (y * u),
    Math.sqrt(1 / x + 1 / y + 1 / u + 1 / v),
    corrected,
    zFor(level)
  );
}

export function riskRatio(
  a: number,
  b: number,
  c: number,
  d: number,
  level = 0.95
): RatioEstimate | null {
  if (a + b === 0 || c + d === 0 || a + c === 0 || b + d === 0) {
    return null;
  }
  const corrected = a === 0 || c === 0;
  const [x, y, u, v] = corrected
    ? [a + 0.5, b + 0.5, c + 0.5, d + 0.5]
    : [a, b, c, d];
  return ratioEstimate(
    x / (x + y) / (u / (u + v)),
    Math.sqrt(1 / x - 1 / (x + y) + 1 / u - 1 / (u + v)),
    corrected,
    zFor(level)
  );
}

function numberNeeded(rd: number, lower: number, upper: number): NumberNeeded {
  let direction: NumberNeeded["direction"] = "none";
  if (rd > 0) {
    direction = "harm";
  } else if (rd < 0) {
    direction = "benefit";
  }
  const value = rd === 0 ? Number.POSITIVE_INFINITY : 1 / Math.abs(rd);
  if (lower > 0 || upper < 0) {
    const ends = [1 / Math.abs(lower), 1 / Math.abs(upper)].sort(
      (x, y) => x - y
    );
    return {
      value,
      direction,
      interval: { kind: "bounded", lower: ends[0], upper: ends[1] },
    };
  }
  return {
    value,
    direction,
    interval: {
      kind: "split",
      towardsBenefit: 1 / Math.abs(lower),
      towardsHarm: 1 / Math.abs(upper),
    },
  };
}

export function riskMeasures(
  a: number,
  b: number,
  c: number,
  d: number,
  level = 0.95
): RiskResult {
  const n1 = a + b;
  const n0 = c + d;
  const riskExposed = n1 > 0 ? a / n1 : Number.NaN;
  const riskUnexposed = n0 > 0 ? c / n0 : Number.NaN;
  let riskDifference: RiskResult["riskDifference"] = null;
  let needed: NumberNeeded | null = null;
  if (n1 > 0 && n0 > 0) {
    const value = riskExposed - riskUnexposed;
    const se = Math.sqrt(
      (riskExposed * (1 - riskExposed)) / n1 +
        (riskUnexposed * (1 - riskUnexposed)) / n0
    );
    const z = zFor(level);
    riskDifference = {
      value,
      se,
      lower: value - z * se,
      upper: value + z * se,
    };
    needed =
      se > 0
        ? numberNeeded(value, riskDifference.lower, riskDifference.upper)
        : null;
  }
  return {
    a,
    b,
    c,
    d,
    riskExposed,
    riskUnexposed,
    oddsRatio: oddsRatio(a, b, c, d, level),
    riskRatio: riskRatio(a, b, c, d, level),
    riskDifference,
    numberNeeded: needed,
  };
}
